import React, { useEffect, useState, useCallback } from "react";
import { Navigate, Link } from "react-router-dom";
import { useAdmin } from "../context/AdminContext";
import DropZone from "../components/DropZone";
import {
  LogOut, Settings, Package, ShoppingBag, Archive, Loader2,
  CheckCircle2, AlertTriangle, RefreshCw, Save, Sparkles, TrendingUp, TrendingDown,
  Database, Truck, Search, ExternalLink, Copy, Image as ImageIcon, Upload, X, Trash2,
  Layers, Users, Scale, Globe,
} from "lucide-react";

const TABS = [
  { key: "pricing", label: "Prijsmotor", icon: Settings },
  { key: "price-per-ton", label: "Prijs / ton", icon: Scale },
  { key: "catalog", label: "Catalogus", icon: Package },
  { key: "images", label: "Afbeeldingen", icon: ImageIcon },
  { key: "orders", label: "Orders", icon: ShoppingBag },
  { key: "distributors", label: "Distributeurs", icon: Users },
  { key: "backups", label: "Backups", icon: Archive },
  { key: "transport", label: "Transport", icon: Truck },
  { key: "seo", label: "SEO", icon: Search },
];

export default function AdminPage() {
  const { admin, logout, adminApi } = useAdmin();
  const [tab, setTab] = useState(() => {
    try {
      const q = new URLSearchParams(window.location.search).get("tab");
      const valid = TABS.map((t) => t.key);
      return q && valid.includes(q) ? q : "pricing";
    } catch {
      return "pricing";
    }
  });

  if (admin === null) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="animate-spin text-nibro-sage-dark" />
      </div>
    );
  }
  if (!admin || admin.role !== "admin") {
    return <Navigate to="/admin/login" replace />;
  }

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="admin-page">
      <header className="border-b-2 border-aramfix-red/30 bg-nibro-sage">
        <div className="mx-auto flex max-w-[1400px] items-center justify-between px-5 py-4 lg:px-10">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/80">
              NIBRO · Admin Panel
            </div>
            <div className="font-display text-xl font-bold text-white">
              Dashboard
            </div>
          </div>
          <div className="flex items-center gap-3 text-white">
            <span className="font-mono text-xs text-white/80">{admin.email}</span>
            <Link
              to="/"
              className="rounded-md bg-white/10 px-3 py-1.5 text-xs font-bold uppercase tracking-wide hover:bg-white/20"
              data-testid="admin-back-to-site"
            >
              ← Site
            </Link>
            <button
              type="button"
              onClick={logout}
              className="flex items-center gap-1.5 rounded-md bg-aramfix-red/90 px-3 py-1.5 text-xs font-bold uppercase tracking-wide text-white hover:bg-aramfix-red"
              data-testid="admin-logout-btn"
            >
              <LogOut size={12} /> Uitloggen
            </button>
          </div>
        </div>
        <nav className="mx-auto flex max-w-[1400px] gap-1 overflow-x-auto px-5 lg:px-10">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                data-testid={`admin-tab-${t.key}`}
                className={`flex items-center gap-2 border-b-2 px-4 py-3 text-sm font-bold uppercase tracking-wide transition ${
                  tab === t.key
                    ? "border-aramfix-red text-white"
                    : "border-transparent text-white/70 hover:text-white"
                }`}
              >
                <Icon size={14} /> {t.label}
              </button>
            );
          })}
        </nav>
      </header>

      <main className="mx-auto max-w-[1400px] px-5 py-8 lg:px-10">
        {tab === "pricing" && <PricingTab api={adminApi} />}
        {tab === "price-per-ton" && <PricePerTonTab api={adminApi} />}
        {tab === "catalog" && <CatalogTab api={adminApi} />}
        {tab === "images" && <ImagesTab api={adminApi} />}
        {tab === "orders" && <OrdersTab api={adminApi} />}
        {tab === "distributors" && <DistributorsTab api={adminApi} />}
        {tab === "backups" && <BackupsTab api={adminApi} />}
        {tab === "transport" && <TransportTab api={adminApi} />}
        {tab === "seo" && <SeoTab api={adminApi} />}
      </main>
    </div>
  );
}

// ============= PRICING =============

function PricingTab({ api }) {
  const [cfg, setCfg] = useState(null);
  const [draft, setDraft] = useState(null);
  const [audit, setAudit] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    api.get("/admin/pricing-config").then((r) => {
      setCfg(r.data.config);
      setDraft(r.data.config);
    });
  }, [api]);

  // Debounced audit
  useEffect(() => {
    if (!draft) return;
    const t = setTimeout(async () => {
      setAuditLoading(true);
      try {
        const r = await api.post("/admin/pricing-audit", draft);
        setAudit(r.data);
      } finally {
        setAuditLoading(false);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [draft, api]);

  const update = (k, v) => setDraft({ ...draft, [k]: v });

  const save = async () => {
    setSaving(true);
    setSaved(false);
    try {
      const r = await api.put("/admin/pricing-config", draft);
      setCfg(r.data.config);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } finally {
      setSaving(false);
    }
  };

  if (!cfg || !draft) {
    return <div className="text-center text-zinc-500"><Loader2 className="mx-auto animate-spin" /></div>;
  }

  const dirty = JSON.stringify(cfg) !== JSON.stringify(draft);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-2xl font-bold">Prijsmotor configuratie</h2>
        <button
          type="button"
          disabled={!dirty || saving}
          onClick={save}
          className={`flex items-center gap-2 rounded-md px-5 py-2.5 text-sm font-bold text-white transition ${
            dirty && !saving ? "bg-nibro-sage hover:bg-nibro-sage-hover" : "cursor-not-allowed bg-zinc-300"
          }`}
          data-testid="pricing-save"
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> :
           saved ? <CheckCircle2 size={14} /> : <Save size={14} />}
          {saved ? "Opgeslagen" : dirty ? "Opslaan" : "Geen wijzigingen"}
        </button>
      </div>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        {/* Transport */}
        <Card title="Transport markup" icon={Truck}>
          <NumberInput
            label="Markup × inkoopprijs"
            value={draft.transportMarkup}
            onChange={(v) => update("transportMarkup", v)}
            step={0.05}
            testId="transport-markup"
            help="1.5 = 50% marge op TFF broker prijzen"
          />
        </Card>

        {/* Product margin */}
        <Card title="Product marge" icon={TrendingUp}>
          <div className="mb-3 flex gap-2">
            {["fixed", "dynamic"].map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => update("productMarkupMode", m)}
                className={`flex-1 rounded-md px-3 py-2 text-xs font-bold uppercase tracking-wide transition ${
                  draft.productMarkupMode === m
                    ? "bg-nibro-sage text-white"
                    : "border border-zinc-300 bg-white text-zinc-600 hover:bg-zinc-50"
                }`}
                data-testid={`mode-${m}`}
              >
                {m === "fixed" ? "Vast" : "Dynamisch"}
              </button>
            ))}
          </div>
          {draft.productMarkupMode === "fixed" ? (
            <NumberInput
              label="Vaste markup × kostprijs"
              value={draft.productMarkupFixed}
              onChange={(v) => update("productMarkupFixed", v)}
              step={0.1}
              testId="markup-fixed"
              help="3.0 = 200% marge op huidige Excel kostprijzen"
            />
          ) : (
            <div className="space-y-3">
              <NumberInput
                label="Minimum"
                value={draft.productMarkupMin}
                onChange={(v) => update("productMarkupMin", v)}
                step={0.1}
                testId="markup-min"
              />
              <NumberInput
                label="Maximum"
                value={draft.productMarkupMax}
                onChange={(v) => update("productMarkupMax", v)}
                step={0.1}
                testId="markup-max"
              />
              <div className="rounded border border-zinc-200 bg-zinc-50 p-3 text-[11px] text-zinc-600">
                Populaire producten (hoge views + orders, {draft.popularityWindowDays}d) krijgen max markup; obscure producten krijgen min.
              </div>
            </div>
          )}
        </Card>

        {/* Weekknallers */}
        <Card title="Weekknallers" icon={Sparkles}>
          <label className="mb-3 flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={draft.weekknallerEnabled}
              onChange={(e) => update("weekknallerEnabled", e.target.checked)}
              className="h-4 w-4 accent-nibro-sage"
              data-testid="weekknaller-enabled"
            />
            <span className="font-semibold">Inschakelen</span>
          </label>
          <NumberInput
            label="Korting markup"
            value={draft.weekknallerMarkup}
            onChange={(v) => update("weekknallerMarkup", v)}
            step={0.1}
            testId="weekknaller-markup"
            help="Lagere marge op overstock (bv. 2.2 i.p.v. 3.0)"
          />
          <NumberInput
            label="Max aantal"
            value={draft.weekknallerMaxCount}
            onChange={(v) => update("weekknallerMaxCount", v, true)}
            step={1}
            testId="weekknaller-count"
          />
        </Card>

        {/* Omdoos-korting config */}
        <Card title="Omdoos-korting" icon={Sparkles}>
          <NumberInput
            label="Kortingspercentage (%)"
            value={draft.boxDiscountPct}
            onChange={(v) => update("boxDiscountPct", v)}
            step={0.5}
            testId="box-discount-pct"
            help="Korting geldt alléén op veelvouden van het omdoos-aantal (75 · 150 · 225 …). 76 stuks = stukprijs."
          />
          <NumberInput
            label="Max gewicht per omdoos (kg)"
            value={draft.boxMaxWeightKg}
            onChange={(v) => update("boxMaxWeightKg", v)}
            step={1}
            testId="box-max-weight"
            help="Bepaalt het aantal stuks per omdoos op basis van variant-gewicht."
          />
          <div className="mt-2 rounded border border-zinc-200 bg-zinc-50 p-3 text-[11px] text-zinc-600">
            Voorbeeld bij {(draft.boxMaxWeightKg || 20).toFixed(0)}kg-omdoos: 60 g/stuk → 300 stuks/omdoos.
            Staffel: 300 · 600 · 900 stuks krijgen {(draft.boxDiscountPct || 10).toFixed(1)}% korting.
            <br />Tussenliggende aantallen (bv. 301) betalen volledige stukprijs.
          </div>
          <NumberInput
            label="Split-pack toeslag (€)"
            value={draft.subpackSurcharge}
            onChange={(v) => update("subpackSurcharge", v)}
            step={0.5}
            testId="subpack-surcharge"
            help="Toeslag per bestelregel wanneer de bestelhoeveelheid geen veelvoud is van de verpakkingseenheid (bv. VE=10, bestel 8 of 12 → +€24.50)."
          />
        </Card>

        {/* Configurator (maatwerk) — €/ton × factor */}
        <Card title="Configurator (maatwerk)" icon={Settings}>
          <NumberInput
            label="Markup factor (×)"
            value={draft.configuratorMarkupFactor}
            onChange={(v) => update("configuratorMarkupFactor", v)}
            step={0.1}
            testId="configurator-markup-factor"
            help="Vermenigvuldigd met de €/ton van de dichtstbijzijnde productie-SKU (zelfde stripmaat + nabije diameter). Bv. 11.000€/ton × 3 = 33.000€/ton voor maatwerk-beugel."
          />
          <NumberInput
            label="Minimum order waarde (€)"
            value={draft.configuratorMinOrderEur}
            onChange={(v) => update("configuratorMinOrderEur", v)}
            step={25}
            testId="configurator-min-order"
            help="Bepaalt het minimale aantal stuks dat de klant moet bestellen — wordt automatisch berekend uit deze waarde gedeeld door de stukprijs."
          />
          <div className="mt-2 rounded border border-zinc-200 bg-zinc-50 p-3 text-[11px] text-zinc-600">
            Bv. Ø60.3 / 30x4mm / 304 weegt 192g. Referentie €/ton (304, 30x4mm) × factor {(draft.configuratorMarkupFactor || 3).toFixed(1)}
            → stukprijs ≈ €{(((draft.configuratorMarkupFactor || 3) * 17000) * 0.192 / 1000).toFixed(2)}.
            <br />Min order €{(draft.configuratorMinOrderEur || 500).toFixed(0)} → min qty wordt automatisch berekend.
          </div>
        </Card>

        {/* Materiaal zichtbaarheid */}
        <Card title="Materiaal zichtbaarheid" icon={Layers}>
          <p className="mb-3 text-[11px] text-zinc-500">
            Bepaalt welke materialen zichtbaar zijn in de catalogus, op de
            productpagina en in de materiaal-switcher. Uitgevinkte materialen
            blijven in de database, maar worden niet meer getoond aan klanten.
          </p>
          <div className="space-y-2">
            {[
              { key: "304", label: "RVS 304 (V2A)" },
              { key: "316", label: "RVS 316 (V4A)" },
              { key: "galvanized", label: "Verzinkt (Galva)" },
              { key: "black", label: "Zwart staal" },
            ].map((m) => {
              const v = (draft.materialVisibility || {})[m.key] !== false;
              return (
                <label
                  key={m.key}
                  className="flex cursor-pointer items-center justify-between rounded border border-zinc-200 bg-zinc-50 px-3 py-2 text-sm hover:bg-white"
                  data-testid={`material-visibility-row-${m.key}`}
                >
                  <span className="font-semibold text-zinc-800">{m.label}</span>
                  <input
                    type="checkbox"
                    checked={v}
                    onChange={(e) =>
                      update("materialVisibility", {
                        ...(draft.materialVisibility || {}),
                        [m.key]: e.target.checked,
                      })
                    }
                    className="h-4 w-4 accent-nibro-sage"
                    data-testid={`material-visibility-${m.key}`}
                  />
                </label>
              );
            })}
          </div>
        </Card>

        {/* Stock-status per artikelgroep */}
        <StockStatusCard draft={draft} update={update} api={api} />
      </div>

      {/* Audit */}
      <div className="rounded-md border border-zinc-200 bg-white p-6">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="font-display text-lg font-bold">What-if audit</h3>
          {auditLoading && <Loader2 size={14} className="animate-spin text-nibro-sage-dark" />}
        </div>
        {audit ? (
          <div className="space-y-5">
            {audit.warnings.length > 0 && (
              <div className="flex items-start gap-2 rounded-md border-2 border-aramfix-red/40 bg-aramfix-red/5 px-4 py-3 text-xs text-aramfix-red">
                <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
                <div>
                  <strong>{audit.warnings.length} waarschuwing(en):</strong>
                  <ul className="mt-1 list-inside list-disc">
                    {audit.warnings.slice(0, 3).map((w, i) => <li key={i}>{w}</li>)}
                  </ul>
                </div>
              </div>
            )}

            {/* Category stats */}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-[10px] uppercase tracking-wider text-zinc-500">
                    <th className="py-2">Categorie</th>
                    <th className="py-2 text-right">Varianten</th>
                    <th className="py-2 text-right">Gem. marge</th>
                    <th className="py-2 text-right">Totaal marge pool</th>
                  </tr>
                </thead>
                <tbody>
                  {audit.category_stats.map((c) => (
                    <tr key={c.category} className="border-b border-zinc-50">
                      <td className="py-2 font-mono">{c.category}</td>
                      <td className="py-2 text-right font-mono">{c.variants}</td>
                      <td className="py-2 text-right font-mono">€{c.avg_margin.toFixed(2)}</td>
                      <td className="py-2 text-right font-mono text-nibro-sage-dark font-bold">€{c.total_margin_pool.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Top/bottom */}
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
              <TopList title="Top 5 marge per stuk" icon={TrendingUp} rows={audit.top_earners} />
              <TopList title="Laagste 5 marges" icon={TrendingDown} rows={audit.lowest_margins} />
            </div>

            <div className="rounded border border-nibro-sage/30 bg-nibro-sage-pale px-4 py-3 text-xs text-nibro-sage-dark">
              <strong>{audit.weekknaller_count}</strong> weekknallers geselecteerd · windows {draft.popularityWindowDays} dagen
            </div>
          </div>
        ) : (
          <div className="text-sm text-zinc-500">Audit wordt berekend...</div>
        )}
      </div>
    </div>
  );
}

function NumberInput({ label, value, onChange, step = 0.1, testId, help, integer = false }) {
  return (
    <div className="mb-3">
      <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-zinc-600">{label}</label>
      <input
        type="number"
        value={value}
        step={step}
        onChange={(e) => {
          const v = integer ? parseInt(e.target.value || 0) : parseFloat(e.target.value || 0);
          onChange(isNaN(v) ? 0 : v);
        }}
        className="w-full rounded-md border border-zinc-200 bg-white px-3 py-2 text-sm focus:border-nibro-sage focus:outline-none"
        data-testid={`pricing-${testId}`}
      />
      {help && <div className="mt-1 text-[10px] text-zinc-500">{help}</div>}
    </div>
  );
}

function Card({ title, icon: Icon, children }) {
  return (
    <div className="rounded-md border border-zinc-200 bg-white p-5">
      <div className="mb-4 flex items-center gap-2">
        <Icon size={16} className="text-nibro-sage-dark" />
        <h3 className="font-display text-base font-bold">{title}</h3>
      </div>
      {children}
    </div>
  );
}

function TopList({ title, icon: Icon, rows }) {
  return (
    <div className="rounded border border-zinc-200 bg-white">
      <div className="flex items-center gap-2 border-b border-zinc-100 px-3 py-2 text-xs font-bold uppercase tracking-wider text-zinc-600">
        <Icon size={12} /> {title}
      </div>
      <table className="w-full text-xs">
        <tbody>
          {rows.slice(0, 5).map((r) => (
            <tr key={r.sku} className="border-b border-zinc-50">
              <td className="px-3 py-2 font-mono">{r.sku}</td>
              <td className="px-3 py-2 text-zinc-600 truncate max-w-[140px]" title={r.product}>{r.product}</td>
              <td className="px-3 py-2 text-right font-mono">€{r.cost}</td>
              <td className="px-3 py-2 text-right font-mono font-bold">€{r.sale}</td>
              <td className="px-3 py-2 text-right font-mono text-nibro-sage-dark">+€{r.margin.toFixed(2)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ============= STOCK STATUS PER ARTICLE GROUP =============

const STOCK_OPTIONS = [
  { value: "in_stock", label: "24u leverbaar", dot: "bg-emerald-500" },
  { value: "lead_time", label: "1-3 werkdagen", dot: "bg-amber-500" },
  { value: "on_request", label: "Op aanvraag", dot: "bg-zinc-400" },
];

function StockStatusCard({ draft, update, api }) {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/article-groups")
      .then((r) => setGroups(r.data.groups || []))
      .catch(() => setGroups([]))
      .finally(() => setLoading(false));
  }, [api]);

  const cfg = draft.stockStatusByGroup || {};

  const setStatus = (key, value) => {
    const next = { ...cfg };
    if (value === "in_stock") {
      // Default — remove the override so the config stays compact
      delete next[key];
    } else {
      next[key] = value;
    }
    update("stockStatusByGroup", next);
  };

  return (
    <Card title="Voorraad-status per artikelgroep" icon={Database}>
      <p className="mb-3 text-[11px] text-zinc-500">
        Bepaalt de groen/oranje/grijs voorraad-indicator op de catalogus-kaart,
        DN-chips en productpagina. Default is <strong>24u leverbaar</strong>.
      </p>
      {loading ? (
        <div className="flex justify-center py-4"><Loader2 size={16} className="animate-spin text-zinc-400" /></div>
      ) : (
        <div className="max-h-[300px] space-y-2 overflow-y-auto pr-1">
          {groups.map((g) => {
            const current = cfg[(g.article_group_key || "").toLowerCase()] || "in_stock";
            const opt = STOCK_OPTIONS.find((o) => o.value === current) || STOCK_OPTIONS[0];
            return (
              <div
                key={g.article_group_key}
                className="flex items-center justify-between gap-2 rounded border border-zinc-200 bg-zinc-50 px-2.5 py-1.5 text-xs"
                data-testid={`stock-row-${g.article_group_key}`}
              >
                <div className="flex min-w-0 items-center gap-1.5">
                  <span className={`h-2 w-2 shrink-0 rounded-full ${opt.dot}`} />
                  <span className="truncate font-semibold text-zinc-800" title={g.display_name}>
                    {g.display_name}
                  </span>
                </div>
                <select
                  value={current}
                  onChange={(e) => setStatus((g.article_group_key || "").toLowerCase(), e.target.value)}
                  className="shrink-0 rounded border border-zinc-300 bg-white px-1.5 py-0.5 text-[11px] focus:border-nibro-sage focus:outline-none"
                  data-testid={`stock-select-${g.article_group_key}`}
                >
                  {STOCK_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}



// ============= PRICE PER TON =============

function PricePerTonTab({ api }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterMat, setFilterMat] = useState("all");
  const [filterStrip, setFilterStrip] = useState("all");
  const [search, setSearch] = useState("");

  useEffect(() => {
    setLoading(true);
    api.get("/admin/price-per-ton")
      .then((r) => setData(r.data))
      .catch(() => setData({ rows: [], count: 0 }))
      .finally(() => setLoading(false));
  }, [api]);

  if (loading) return <div className="flex items-center gap-2 text-zinc-500"><Loader2 className="animate-spin" size={16} /> Prijslijst laden…</div>;
  if (!data || !data.rows.length) return <div className="text-zinc-500">Geen prijsdata.</div>;

  const stripSizes = Array.from(new Set(data.rows.map((r) => r.strip_size).filter(Boolean))).sort();

  const filtered = data.rows.filter((r) => {
    if (filterMat !== "all" && r.material !== filterMat) return false;
    if (filterStrip !== "all" && r.strip_size !== filterStrip) return false;
    if (search) {
      const s = search.toLowerCase();
      if (!r.sku?.toLowerCase().includes(s)
          && !String(r.diameter_mm || "").includes(s)
          && !String(r.dn || "").includes(s)) return false;
    }
    return true;
  });

  const exportCsv = () => {
    const header = ["SKU","Materiaal","Diameter mm","DN","Inch","Stripmaat","Gewicht g","Stukprijs €","€/ton"];
    const lines = [header.join(";"), ...filtered.map((r) => [
      r.sku, r.material, r.diameter_mm, r.dn || "", r.inch || "",
      r.strip_size, r.weight_g, r.unit_price_eur, r.eur_per_ton,
    ].join(";"))];
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `prijslijst_per_ton_${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6" data-testid="admin-price-per-ton-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl font-bold text-zinc-900">Prijslijst per ton</h2>
          <p className="mt-1 text-sm text-zinc-600">
            {data.count} varianten · mediane <strong className="font-mono">€{data.median_eur_per_ton?.toLocaleString("nl-NL")}/ton</strong>.
            Gebruikt als referentie voor de maatwerk-configurator.
          </p>
        </div>
        <button
          type="button"
          onClick={exportCsv}
          className="inline-flex items-center gap-2 rounded-md bg-nibro-sage px-4 py-2 text-sm font-bold uppercase tracking-wider text-white hover:bg-nibro-sage-hover"
          data-testid="price-per-ton-export"
        >
          <Database size={14} /> Exporteer CSV
        </button>
      </div>

      <div className="flex flex-wrap gap-3 rounded-md border border-zinc-200 bg-white p-3">
        <input
          type="text"
          placeholder="Zoek SKU / Ø / DN…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="flex-1 min-w-[180px] rounded border border-zinc-200 px-3 py-2 font-mono text-sm"
          data-testid="price-per-ton-search"
        />
        <select
          value={filterMat}
          onChange={(e) => setFilterMat(e.target.value)}
          className="rounded border border-zinc-200 px-3 py-2 text-sm"
          data-testid="price-per-ton-filter-material"
        >
          <option value="all">Alle materialen</option>
          <option value="304">304 / V2A</option>
          <option value="316">316 / V4A</option>
        </select>
        <select
          value={filterStrip}
          onChange={(e) => setFilterStrip(e.target.value)}
          className="rounded border border-zinc-200 px-3 py-2 text-sm"
          data-testid="price-per-ton-filter-strip"
        >
          <option value="all">Alle stripmaten</option>
          {stripSizes.map((s) => (<option key={s} value={s}>{s}</option>))}
        </select>
      </div>

      <div className="overflow-hidden rounded-md border border-zinc-200">
        <table className="w-full text-sm" data-testid="price-per-ton-table">
          <thead className="bg-zinc-100 font-mono text-[10px] uppercase tracking-widest text-zinc-700">
            <tr>
              <th className="px-3 py-2 text-left">SKU</th>
              <th className="px-3 py-2 text-left">Mat.</th>
              <th className="px-3 py-2 text-right">Ø mm</th>
              <th className="px-3 py-2 text-left">DN / Inch</th>
              <th className="px-3 py-2 text-left">Stripmaat</th>
              <th className="px-3 py-2 text-right">Gewicht g</th>
              <th className="px-3 py-2 text-right">Stukprijs €</th>
              <th className="px-3 py-2 text-right bg-nibro-sage-pale">€/ton</th>
            </tr>
          </thead>
          <tbody className="font-mono text-xs">
            {filtered.slice(0, 500).map((r) => (
              <tr key={r.sku} className="border-t border-zinc-100 hover:bg-nibro-sage-pale/30" data-testid={`price-per-ton-row-${r.sku}`}>
                <td className="px-3 py-1.5 font-bold text-zinc-900">{r.sku}</td>
                <td className="px-3 py-1.5">{r.material}</td>
                <td className="px-3 py-1.5 text-right">{r.diameter_mm}</td>
                <td className="px-3 py-1.5">{r.dn ? `DN${r.dn}` : ""}{r.dn && r.inch ? " · " : ""}{r.inch || ""}</td>
                <td className="px-3 py-1.5">{r.strip_size}</td>
                <td className="px-3 py-1.5 text-right">{r.weight_g}</td>
                <td className="px-3 py-1.5 text-right">€{r.unit_price_eur.toFixed(2)}</td>
                <td className="px-3 py-1.5 text-right font-bold text-nibro-sage-dark bg-nibro-sage-pale/40">
                  €{r.eur_per_ton.toLocaleString("nl-NL")}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length > 500 && (
          <div className="border-t border-zinc-200 bg-zinc-50 px-3 py-2 text-center font-mono text-[10px] text-zinc-500">
            Eerste 500 van {filtered.length} getoond — exporteer CSV voor volledige lijst.
          </div>
        )}
      </div>
    </div>
  );
}


// ============= CATALOG =============

function CatalogTab({ api }) {
  const [file, setFile] = useState(null);
  const [status, setStatus] = useState("");
  const [result, setResult] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);

  const upload = async () => {
    if (!file) return;
    setUploading(true);
    setStatus("");
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/admin/catalog/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120_000,
      });
      setResult(r.data);
      setFile(null);
      setStatus(`✓ Geüpload: ${r.data.families} families · ${r.data.variants} SKUs · ${r.data.products_loaded} producten geladen`);
    } catch (e) {
      const d = e.response?.data?.detail;
      setStatus("Fout: " + (typeof d === "string" ? d : e.message));
    } finally {
      setUploading(false);
    }
  };

  const reload = async () => {
    setLoading(true);
    try {
      const r = await api.post("/admin/catalog/reload");
      setStatus(`Catalogus herladen — ${r.data.products} producten`);
    } catch (e) {
      setStatus("Fout: " + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <h2 className="font-display text-2xl font-bold">Catalogus beheer</h2>

      <div className="rounded-md border border-zinc-200 bg-white p-6">
        <div className="mb-2 font-display font-bold">Excel prijslijst uploaden</div>
        <p className="mb-4 text-sm text-zinc-600">
          Sleep een nieuwe <code className="rounded bg-zinc-100 px-1.5 py-0.5 font-mono text-xs">.xlsx</code> prijslijst hierheen. Verwachte kolommen: <code className="font-mono text-xs">Artikel</code> · <code className="font-mono text-xs">Omschrijving</code> · <code className="font-mono text-xs">Verkoopprijs variabel</code> · <code className="font-mono text-xs">Transport gewicht</code>.
          <br/>Na upload wordt automatisch de catalog opnieuw opgebouwd, de in-memory cache ververst en de sitemap geregenereerd.
        </p>
        <DropZone file={file} onChange={setFile} accept=".xlsx,.xls" testId="catalog" />
        <button
          type="button"
          onClick={upload}
          disabled={!file || uploading}
          className={`mt-4 flex items-center gap-2 rounded-md px-5 py-2.5 text-sm font-bold text-white transition ${
            !file || uploading ? "cursor-not-allowed bg-zinc-300" : "bg-nibro-sage hover:bg-nibro-sage-hover"
          }`}
          data-testid="catalog-upload-btn"
        >
          {uploading
            ? <><Loader2 size={14} className="animate-spin" /> Uploaden + parsen...</>
            : <><Database size={14} /> Upload &amp; rebuild catalog</>}
        </button>
        {result && (
          <div className="mt-4 rounded-md border border-emerald-300 bg-emerald-50 p-4 text-sm text-emerald-900" data-testid="upload-result">
            <div className="font-bold">✓ Catalogus succesvol bijgewerkt</div>
            <div className="mt-1.5 grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
              <div>Bestand: <span className="font-mono">{result.filename}</span></div>
              <div>Families: <strong>{result.families}</strong></div>
              <div>SKUs: <strong>{result.variants}</strong></div>
              <div>Producten geladen: <strong>{result.products_loaded}</strong></div>
              <div className="col-span-2 mt-1 text-emerald-800">Categorieën: {(result.categories || []).join(" · ")}</div>
              <div className="col-span-2 text-emerald-800">Materialen: {(result.materials || []).join(" · ")}</div>
            </div>
          </div>
        )}
      </div>

      <div className="rounded-md border border-zinc-200 bg-white p-6">
        <div className="mb-2 font-display font-bold">In-memory catalogus herladen</div>
        <p className="mb-4 text-sm text-zinc-600">
          Forceer een herlaad van <code className="font-mono text-xs">catalog.json</code> zonder backend restart (bijvoorbeeld na handmatige update via SSH).
        </p>
        <button
          type="button"
          onClick={reload}
          disabled={loading}
          className="flex items-center gap-2 rounded-md bg-nibro-sage px-5 py-2.5 text-sm font-bold text-white hover:bg-nibro-sage-hover disabled:opacity-50"
          data-testid="catalog-reload-btn"
        >
          {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
          Herlaad catalogus
        </button>
        {status && (
          <div className="mt-3 rounded border border-nibro-sage/30 bg-nibro-sage-pale px-3 py-2 text-xs text-nibro-sage-dark" data-testid="catalog-status">
            {status}
          </div>
        )}
      </div>
    </div>
  );
}

// ============= ORDERS =============

function OrdersTab({ api }) {
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    const [ord, st] = await Promise.all([
      api.get("/admin/orders?limit=100"),
      api.get("/admin/orders/stats"),
    ]);
    setOrders(ord.data.orders);
    setStats(st.data);
    setLoading(false);
  }, [api]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-2xl font-bold">Orders</h2>
        <button onClick={fetchAll} className="flex items-center gap-2 rounded-md bg-nibro-sage px-4 py-2 text-xs font-bold text-white hover:bg-nibro-sage-hover" data-testid="orders-refresh">
          <RefreshCw size={12} /> Verversen
        </button>
      </div>

      {stats && (
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <Stat label="All time" n={stats.all_time?.n || 0} total={stats.all_time?.total || 0} />
          <Stat label="Laatste 30 dagen" n={stats.month?.n || 0} total={stats.month?.total || 0} />
          <Stat label="Laatste 7 dagen" n={stats.week?.n || 0} total={stats.week?.total || 0} />
          <Stat label="Status" n={stats.by_status?.length || 0} suffix="statussen" />
        </div>
      )}

      <div className="overflow-x-auto rounded-md border border-zinc-200 bg-white">
        <table className="w-full text-sm">
          <thead className="border-b bg-zinc-50 text-left text-[10px] uppercase tracking-wider text-zinc-500">
            <tr>
              <th className="px-3 py-2">Ordernummer</th>
              <th className="px-3 py-2">Datum</th>
              <th className="px-3 py-2">Klant</th>
              <th className="px-3 py-2">Land</th>
              <th className="px-3 py-2">BTW</th>
              <th className="px-3 py-2 text-right">Totaal</th>
              <th className="px-3 py-2">Status</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} className="px-3 py-6 text-center"><Loader2 className="mx-auto animate-spin text-nibro-sage-dark" /></td></tr>
            )}
            {!loading && orders.length === 0 && (
              <tr><td colSpan={7} className="px-3 py-6 text-center text-zinc-500">Nog geen orders.</td></tr>
            )}
            {orders.map((o) => (
              <tr key={o.order_number} className="border-b border-zinc-50">
                <td className="px-3 py-2 font-mono text-xs">{o.order_number}</td>
                <td className="px-3 py-2 font-mono text-[11px] text-zinc-500">{(o.created_at || "").slice(0, 16).replace("T", " ")}</td>
                <td className="px-3 py-2">{o.customer_name}{o.company ? ` · ${o.company}` : ""}</td>
                <td className="px-3 py-2 font-mono text-xs">{o.shipping_country_code || o.shipping_country}</td>
                <td className="px-3 py-2 font-mono text-xs">
                  {o.vat_reverse_charge ? <span className="text-aramfix-red">0% reverse</span> : `${Math.round((o.vat_rate || 0) * 100)}%`}
                </td>
                <td className="px-3 py-2 text-right font-mono font-bold">€{(o.total || 0).toFixed(2)}</td>
                <td className="px-3 py-2"><span className="rounded bg-amber-100 px-2 py-0.5 font-mono text-[10px] uppercase text-amber-800">{o.status}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Stat({ label, n, total, suffix }) {
  return (
    <div className="rounded-md border border-zinc-200 bg-white p-4">
      <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">{label}</div>
      <div className="mt-1 font-display text-2xl font-bold">{n}</div>
      {total !== undefined && <div className="text-xs text-zinc-500">€{total.toFixed(2)}</div>}
      {suffix && <div className="text-xs text-zinc-500">{suffix}</div>}
    </div>
  );
}

// ============= BACKUPS =============

function BackupsTab({ api }) {
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/backups");
      setBackups(r.data.backups);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    setCreating(true);
    try {
      await api.post("/admin/backups/create");
      await load();
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-2xl font-bold">Backups</h2>
        <button onClick={create} disabled={creating} className="flex items-center gap-2 rounded-md bg-nibro-sage px-4 py-2 text-xs font-bold text-white hover:bg-nibro-sage-hover disabled:opacity-50" data-testid="backup-create">
          {creating ? <Loader2 size={12} className="animate-spin" /> : <Database size={12} />}
          Nieuwe snapshot
        </button>
      </div>
      <div className="rounded-md border border-zinc-200 bg-white">
        {loading && <div className="p-6 text-center"><Loader2 className="mx-auto animate-spin text-nibro-sage-dark" /></div>}
        {!loading && backups.length === 0 && <div className="p-6 text-center text-sm text-zinc-500">Nog geen backups.</div>}
        {backups.length > 0 && (
          <table className="w-full text-sm">
            <thead className="border-b bg-zinc-50 text-left text-[10px] uppercase tracking-wider text-zinc-500">
              <tr>
                <th className="px-3 py-2">Bestand</th>
                <th className="px-3 py-2">Datum</th>
                <th className="px-3 py-2 text-right">Grootte</th>
              </tr>
            </thead>
            <tbody>
              {backups.map((b) => (
                <tr key={b.name} className="border-b border-zinc-50">
                  <td className="px-3 py-2 font-mono text-xs">{b.name}</td>
                  <td className="px-3 py-2 font-mono text-[11px] text-zinc-500">{b.modified.slice(0, 16).replace("T", " ")}</td>
                  <td className="px-3 py-2 text-right font-mono">{b.size_kb} KB</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ============= SEO =============

function SeoTab({ api }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [toast, setToast] = useState("");
  const [pingState, setPingState] = useState({ loading: true, pings: [], indexnow_key: "" });
  const [pingNowBusy, setPingNowBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/sitemap/status");
      setStatus(r.data);
    } finally {
      setLoading(false);
    }
  }, [api]);

  const loadPings = useCallback(async () => {
    try {
      const r = await api.get("/admin/seo/ping-log?limit=10");
      setPingState({ loading: false, pings: r.data.pings || [], indexnow_key: r.data.indexnow_key || "" });
    } catch {
      setPingState({ loading: false, pings: [], indexnow_key: "" });
    }
  }, [api]);

  useEffect(() => { load(); loadPings(); }, [load, loadPings]);

  const regenerate = async () => {
    setRegenerating(true);
    try {
      await api.post("/admin/sitemap/regenerate");
      await Promise.all([load(), loadPings()]);
      setToast("Sitemap.xml + robots.txt opnieuw gegenereerd · Crawlers genotificeerd");
      setTimeout(() => setToast(""), 2800);
    } finally {
      setRegenerating(false);
    }
  };

  const pingNow = async () => {
    setPingNowBusy(true);
    try {
      const r = await api.post("/admin/seo/ping-now");
      const ok = r.data?.indexnow?.ok;
      setToast(ok ? "Crawlers genotificeerd (IndexNow OK)" : "Ping verzonden — zie log voor status");
      await loadPings();
      setTimeout(() => setToast(""), 2800);
    } finally {
      setPingNowBusy(false);
    }
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    setToast("Gekopieerd naar klembord");
    setTimeout(() => setToast(""), 1500);
  };

  if (loading || !status) {
    return <div className="text-center"><Loader2 className="mx-auto animate-spin text-nibro-sage-dark" /></div>;
  }

  const sitemapUrl = `${status.base_url}/sitemap.xml`;
  const robotsUrl = `${status.base_url}/robots.txt`;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-2xl font-bold">SEO &amp; Sitemap</h2>
        <button
          type="button"
          onClick={regenerate}
          disabled={regenerating}
          className="flex items-center gap-2 rounded-md bg-nibro-sage px-4 py-2 text-xs font-bold text-white hover:bg-nibro-sage-hover disabled:opacity-50"
          data-testid="seo-regenerate-btn"
        >
          {regenerating ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
          Herbouwen
        </button>
      </div>

      {toast && (
        <div className="rounded-md border border-emerald-300 bg-emerald-50 px-4 py-2 text-xs text-emerald-800" data-testid="seo-toast">
          {toast}
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <StatCard label="Public URL" value={status.base_url} testId="seo-base-url" />
        <StatCard label="URLs in sitemap" value={status.sitemap_url_count || 0} />
        <StatCard
          label="Sitemap grootte"
          value={status.sitemap_size_bytes ? `${(status.sitemap_size_bytes / 1024).toFixed(1)} KB` : "—"}
        />
      </div>

      <div className="rounded-md border border-zinc-200 bg-white p-6">
        <div className="mb-4 font-display font-bold">Crawler-URLs</div>
        <div className="space-y-3">
          <UrlRow
            label="sitemap.xml"
            url={sitemapUrl}
            ok={status.sitemap_exists}
            onCopy={() => copy(sitemapUrl)}
            testId="seo-sitemap-url"
          />
          <UrlRow
            label="robots.txt"
            url={robotsUrl}
            ok={status.robots_exists}
            onCopy={() => copy(robotsUrl)}
            testId="seo-robots-url"
          />
        </div>
        <p className="mt-4 text-xs text-zinc-500">
          Laatste regeneratie: <span className="font-mono">{(status.sitemap_modified || "").slice(0, 16).replace("T", " ")}</span>
        </p>
      </div>

      <div className="rounded-md border border-zinc-200 bg-white p-6">
        <div className="mb-3 font-display font-bold">Indien-niet-geïndexeerd checklist</div>
        <ol className="list-inside list-decimal space-y-2 text-sm text-zinc-700">
          <li>
            Voeg de sitemap-URL toe in <a href="https://search.google.com/search-console" target="_blank" rel="noreferrer" className="text-nibro-sage-dark underline">Google Search Console</a> → Sitemaps → "Voeg een nieuwe sitemap toe" → plak <code className="rounded bg-zinc-100 px-1.5 py-0.5 font-mono text-xs">{sitemapUrl}</code>
          </li>
          <li>
            Doe hetzelfde in <a href="https://www.bing.com/webmasters" target="_blank" rel="noreferrer" className="text-nibro-sage-dark underline">Bing Webmaster Tools</a>
          </li>
          <li>
            Klik "Herbouwen" hierboven na elke catalogus-update zodat nieuwe SKUs in de sitemap komen
          </li>
          <li>
            Voor productie deploy: zet <code className="rounded bg-zinc-100 px-1.5 py-0.5 font-mono text-xs">PUBLIC_URL=https://stainlesspipeclamps.store</code> (zonder www!) in backend .env zodat de sitemap-URLs matchen met je GSC property
          </li>
        </ol>
      </div>

      {/* Per-language sitemap stats */}
      {status?.base_url && (
        <LanguageSitemapWidget api={api} baseUrl={status.base_url} onCopy={copy} />
      )}

      {status.robots_content && (
        <details className="rounded-md border border-zinc-200 bg-white">
          <summary className="cursor-pointer border-b border-zinc-100 px-4 py-3 text-xs font-bold uppercase tracking-wider text-zinc-600">
            robots.txt inhoud
          </summary>
          <pre className="overflow-x-auto p-4 text-[11px] leading-relaxed text-zinc-700">{status.robots_content}</pre>
        </details>
      )}

      {/* Auto-ping crawlers (IndexNow + Google/Bing) */}
      <div className="rounded-md border border-zinc-200 bg-white p-6" data-testid="seo-ping-panel">
        <div className="mb-3 flex items-center justify-between">
          <div>
            <div className="font-display text-base font-bold">Auto-ping crawlers</div>
            <p className="mt-0.5 text-[11px] text-zinc-500">
              IndexNow notificeert Bing + Yandex + Naver + Seznam direct na elke "Herbouwen".
              Daarnaast draait elke vrijdag 23:00 een automatische ping.
            </p>
          </div>
          <button
            type="button"
            onClick={pingNow}
            disabled={pingNowBusy}
            className="rounded-md border border-nibro-sage bg-white px-3 py-1.5 text-xs font-bold text-nibro-sage-dark hover:bg-nibro-sage-pale disabled:opacity-50"
            data-testid="seo-ping-now-btn"
          >
            {pingNowBusy ? "Bezig..." : "Ping nu"}
          </button>
        </div>
        {pingState.indexnow_key && (
          <div className="mb-3 rounded border border-zinc-200 bg-zinc-50 px-3 py-2 text-[11px]">
            <div className="font-mono uppercase tracking-wider text-zinc-500">IndexNow key</div>
            <div className="mt-0.5 break-all font-mono text-zinc-800">{pingState.indexnow_key}</div>
            <div className="mt-1 text-zinc-500">
              Beschikbaar op <code className="font-mono">{`${status.base_url}/${pingState.indexnow_key}.txt`}</code>
            </div>
          </div>
        )}
        {pingState.pings.length === 0 ? (
          <div className="rounded border border-dashed border-zinc-300 p-4 text-center text-xs text-zinc-500">
            Nog geen ping verzonden. Klik "Ping nu" of wacht tot eerstvolgende "Herbouwen" / vrijdag 23:00.
          </div>
        ) : (
          <div className="overflow-hidden rounded border border-zinc-200">
            <table className="w-full text-[11px]">
              <thead className="bg-zinc-50 text-left text-[10px] uppercase tracking-wider text-zinc-500">
                <tr>
                  <th className="px-3 py-2">Wanneer</th>
                  <th className="px-3 py-2">IndexNow</th>
                  <th className="px-3 py-2">URLs</th>
                  <th className="px-3 py-2">Google</th>
                  <th className="px-3 py-2">Bing</th>
                </tr>
              </thead>
              <tbody>
                {pingState.pings.map((p, idx) => {
                  const status = (s) =>
                    s?.ok
                      ? <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 font-bold text-emerald-700">OK · {s.status_code || ""}</span>
                      : <span className="inline-flex items-center gap-1 rounded-full bg-zinc-100 px-2 py-0.5 font-bold text-zinc-500" title={s?.error || ""}>
                          {s?.status_code || "—"}
                        </span>;
                  return (
                    <tr key={idx} className="border-t border-zinc-100" data-testid={`ping-row-${idx}`}>
                      <td className="px-3 py-2 font-mono text-[10px] text-zinc-600">
                        {(p.at || "").slice(0, 16).replace("T", " ")}
                      </td>
                      <td className="px-3 py-2">{status(p.indexnow)}</td>
                      <td className="px-3 py-2 font-mono text-zinc-700">{p.url_count}</td>
                      <td className="px-3 py-2">{status(p.google)}</td>
                      <td className="px-3 py-2">{status(p.bing)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, testId }) {
  return (
    <div className="rounded-md border border-zinc-200 bg-white p-4" data-testid={testId}>
      <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">{label}</div>
      <div className="mt-1 break-all font-display text-lg font-bold">{value}</div>
    </div>
  );
}

function UrlRow({ label, url, ok, onCopy, testId }) {
  return (
    <div className="flex items-center gap-3 rounded border border-zinc-100 bg-zinc-50 px-3 py-2">
      <div className="font-mono text-xs font-bold text-zinc-600">{label}</div>
      <div className="flex-1 truncate font-mono text-[11px] text-zinc-900" title={url} data-testid={testId}>{url}</div>
      {ok ? (
        <CheckCircle2 size={14} className="flex-shrink-0 text-emerald-600" />
      ) : (
        <AlertTriangle size={14} className="flex-shrink-0 text-amber-500" />
      )}
      <button
        type="button"
        onClick={onCopy}
        className="rounded p-1.5 text-zinc-500 hover:bg-white"
        aria-label="Kopiëren"
        data-testid={`${testId}-copy`}
      >
        <Copy size={12} />
      </button>
      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        className="rounded p-1.5 text-zinc-500 hover:bg-white"
        aria-label="Open"
      >
        <ExternalLink size={12} />
      </a>
    </div>
  );
}

// ============= TRANSPORT =============



function TransportTab({ api }) {
  const [history, setHistory] = useState([]);
  const [running, setRunning] = useState(false);
  const [last, setLast] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/scraper/history").then((r) => setHistory(r.data.history));
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const warmup = async () => {
    setRunning(true);
    try {
      const r = await api.post("/admin/scraper/warmup");
      setLast(r.data);
      load();
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="space-y-6">
      <h2 className="font-display text-2xl font-bold">TFF Transport Scraper</h2>
      <div className="rounded-md border border-zinc-200 bg-white p-6">
        <p className="mb-4 text-sm text-zinc-600">
          Test de TFF Express scraper handmatig. <strong>Let op:</strong> een cold-start kan 60-90 seconden duren (Chromium opstart + TFF login).
        </p>
        <button
          type="button"
          onClick={warmup}
          disabled={running}
          className="flex items-center gap-2 rounded-md bg-nibro-sage px-5 py-2.5 text-sm font-bold text-white hover:bg-nibro-sage-hover disabled:opacity-50"
          data-testid="transport-warmup-btn"
        >
          {running ? <Loader2 size={14} className="animate-spin" /> : <Truck size={14} />}
          Test scraper nu
        </button>
        {last && (
          <div className={`mt-4 rounded-md border px-4 py-3 text-sm ${last.success ? "border-emerald-300 bg-emerald-50 text-emerald-800" : "border-aramfix-red/40 bg-aramfix-red/5 text-aramfix-red"}`}>
            {last.success ? (
              <><strong>{last.options_count} opties</strong> in {last.duration_s}s</>
            ) : (
              <><strong>Fout:</strong> {last.error || "onbekend"} ({last.duration_s}s)</>
            )}
          </div>
        )}
      </div>
      <div className="rounded-md border border-zinc-200 bg-white">
        <div className="border-b bg-zinc-50 px-4 py-2 text-xs font-bold uppercase tracking-wider text-zinc-600">
          Historie (laatste 20)
        </div>
        {history.length === 0 ? (
          <div className="p-6 text-center text-sm text-zinc-500">Nog geen scraper-runs.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="border-b text-left text-[10px] uppercase tracking-wider text-zinc-500">
              <tr>
                <th className="px-3 py-2">Tijd</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2 text-right">Opties</th>
                <th className="px-3 py-2 text-right">Duur</th>
                <th className="px-3 py-2">Fout</th>
              </tr>
            </thead>
            <tbody>
              {history.slice().reverse().map((h, i) => (
                <tr key={i} className="border-b border-zinc-50">
                  <td className="px-3 py-2 font-mono text-[11px]">{new Date(h.at * 1000).toLocaleString("nl-NL")}</td>
                  <td className="px-3 py-2">
                    {h.success
                      ? <CheckCircle2 size={14} className="text-emerald-600" />
                      : <AlertTriangle size={14} className="text-aramfix-red" />}
                  </td>
                  <td className="px-3 py-2 text-right font-mono">{h.options_count}</td>
                  <td className="px-3 py-2 text-right font-mono">{h.duration_s}s</td>
                  <td className="px-3 py-2 text-xs text-zinc-500">{h.error || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}


/* ============== Images Tab ============== */
function ImagesTab({ api }) {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [drag, setDrag] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [progress, setProgress] = useState([]); // [{name, pct, status}]
  const fileInputRef = React.useRef(null);
  const BASE = process.env.REACT_APP_BACKEND_URL || "";

  const CHUNK_SIZE = 4 * 1024 * 1024; // 4 MB
  const MAX_IMG_BYTES = 5 * 1024 * 1024; // per-image cap (matches backend)
  const MAX_ZIP_BYTES = 200 * 1024 * 1024; // assembled zip cap

  const reload = useCallback(() => {
    setLoading(true);
    api.get("/admin/images")
      .then((r) => setImages(r.data.images || []))
      .finally(() => setLoading(false));
  }, [api]);

  useEffect(() => { reload(); }, [reload]);

  const uploadOne = async (file, onProgress) => {
    const uploadId = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    const total = Math.max(1, Math.ceil(file.size / CHUNK_SIZE));
    let result = null;
    for (let i = 0; i < total; i++) {
      const start = i * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, file.size);
      const blob = file.slice(start, end);
      const fd = new FormData();
      fd.append("upload_id", uploadId);
      fd.append("chunk_index", String(i));
      fd.append("total_chunks", String(total));
      fd.append("filename", file.name);
      fd.append("chunk", blob, `chunk_${i}`);
      const r = await api.post("/admin/images/upload/chunk", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 180_000,
      });
      onProgress?.(Math.round(((i + 1) / total) * 100));
      if (r.data.done) result = r.data;
    }
    return result;
  };

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;

    // Validation
    const isImg = (f) => /\.(jpe?g|png|webp)$/i.test(f.name);
    const isZip = (f) => /\.zip$/i.test(f.name);
    const invalid = files.filter((f) => !isImg(f) && !isZip(f));
    if (invalid.length) {
      setFeedback({ kind: "error", msg: `Niet-ondersteund: ${invalid.map((f) => f.name).join(", ")}. Toegestaan: JPG/PNG/WEBP of .ZIP.` });
      return;
    }
    const tooLarge = files.filter((f) =>
      (isImg(f) && f.size > MAX_IMG_BYTES) || (isZip(f) && f.size > MAX_ZIP_BYTES)
    );
    if (tooLarge.length) {
      setFeedback({ kind: "error", msg: `Te groot: ${tooLarge.map((f) => f.name).join(", ")} (max 5MB per afbeelding, 200MB per ZIP).` });
      return;
    }

    setUploading(true);
    setFeedback(null);
    setProgress(files.map((f) => ({ name: f.name, pct: 0, status: "queue" })));

    let totalSaved = 0;
    const allErrors = [];
    for (let idx = 0; idx < files.length; idx++) {
      const f = files[idx];
      setProgress((prev) => prev.map((p, i) => i === idx ? { ...p, status: "uploading" } : p));
      try {
        const res = await uploadOne(f, (pct) => {
          setProgress((prev) => prev.map((p, i) => i === idx ? { ...p, pct } : p));
        });
        totalSaved += res?.saved_count || 0;
        if (res?.errors?.length) allErrors.push(...res.errors);
        setProgress((prev) => prev.map((p, i) => i === idx ? {
          ...p, pct: 100, status: "done",
          summary: res?.is_zip ? `${res.saved_count} uit ZIP` : "geüpload",
        } : p));
      } catch (e) {
        const msg = e.response?.data?.detail || e.message || "Upload mislukt";
        allErrors.push({ filename: f.name, error: msg });
        setProgress((prev) => prev.map((p, i) => i === idx ? { ...p, status: "error", error: msg } : p));
      }
    }

    setFeedback({
      kind: allErrors.length ? (totalSaved > 0 ? "warn" : "error") : "ok",
      msg: `${totalSaved} afbeelding(en) opgeslagen${allErrors.length ? ` · ${allErrors.length} fout` : ""}.`,
    });
    setUploading(false);
    reload();
  };

  const handleDelete = async (sku) => {
    if (!window.confirm(`Verwijder afbeelding voor SKU ${sku}?`)) return;
    try {
      await api.delete(`/admin/images/${sku}`);
      reload();
    } catch (e) {
      setFeedback({ kind: "error", msg: "Verwijderen mislukt" });
    }
  };

  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-zinc-200 bg-white p-6 shadow-sm" data-testid="images-tab">
        <h2 className="font-display text-xl font-bold text-zinc-900">SKU Afbeeldingen</h2>
        <p className="mt-1 text-sm text-zinc-600">
          Sleep afbeeldingen of een <strong>.zip-bundel</strong> hierheen. <strong>Bestandsnaam = SKU</strong> (bv. <code className="rounded bg-zinc-100 px-1 font-mono text-xs">304C-0139.jpg</code>).
          Grote bestanden worden automatisch in <strong>blokken van 4 MB</strong> geüpload — ZIP-bestanden tot 200 MB met honderden SKU-foto's tegelijk worden ondersteund.
        </p>

        <label
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={(e) => { e.preventDefault(); setDrag(false); handleFiles(e.dataTransfer.files); }}
          className={`mt-5 flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed px-6 py-12 text-center transition ${
            drag ? "border-aramfix-red bg-aramfix-red/5" : "border-zinc-300 bg-zinc-50 hover:border-nibro-sage"
          } ${uploading ? "pointer-events-none opacity-70" : ""}`}
          data-testid="image-dropzone"
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".jpg,.jpeg,.png,.webp,.zip,image/jpeg,image/png,image/webp,application/zip"
            multiple
            className="hidden"
            onChange={(e) => handleFiles(e.target.files)}
            data-testid="image-file-input"
          />
          {uploading ? (
            <>
              <Loader2 className="animate-spin text-nibro-sage-dark" size={32} />
              <div className="mt-3 text-sm font-semibold text-zinc-700">Bezig met uploaden…</div>
            </>
          ) : (
            <>
              <Upload size={32} className="text-zinc-400" />
              <div className="mt-3 text-base font-semibold text-zinc-900">Sleep bestanden hier of klik om te kiezen</div>
              <div className="mt-1 text-xs text-zinc-500">JPG, PNG, WEBP (max 5 MB/stuk) of .ZIP-bundel (max 200 MB) · 4 MB chunked upload</div>
            </>
          )}
        </label>

        {progress.length > 0 && (
          <div className="mt-4 space-y-2" data-testid="image-upload-progress">
            {progress.map((p, i) => (
              <div key={i} className="rounded-md border border-zinc-200 bg-zinc-50 px-3 py-2 text-xs">
                <div className="flex items-center justify-between gap-3">
                  <span className="truncate font-mono font-semibold text-zinc-800" title={p.name}>{p.name}</span>
                  <span className={`font-mono ${p.status === "error" ? "text-aramfix-red" : "text-zinc-600"}`}>
                    {p.status === "error" ? "fout" : p.status === "done" ? (p.summary || "klaar") : `${p.pct}%`}
                  </span>
                </div>
                <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-zinc-200">
                  <div
                    className={`h-full transition-all ${p.status === "error" ? "bg-aramfix-red" : "bg-nibro-sage"}`}
                    style={{ width: `${p.pct}%` }}
                  />
                </div>
                {p.error && <div className="mt-1 text-[11px] text-aramfix-red">{p.error}</div>}
              </div>
            ))}
          </div>
        )}

        {feedback && (
          <div
            className={`mt-4 rounded-md px-4 py-2.5 text-sm font-semibold ${
              feedback.kind === "ok" ? "bg-emerald-50 text-emerald-700"
                : feedback.kind === "warn" ? "bg-amber-50 text-amber-700"
                : "bg-aramfix-red/10 text-aramfix-red"
            }`}
            data-testid="image-upload-feedback"
          >
            {feedback.msg}
          </div>
        )}
      </div>

      <div className="rounded-lg border border-zinc-200 bg-white p-6 shadow-sm">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-lg font-bold text-zinc-900">
            Geüploade overrides ({images.length})
          </h3>
          <button
            type="button"
            onClick={reload}
            className="inline-flex items-center gap-1 text-sm font-semibold text-nibro-sage-dark hover:underline"
            data-testid="image-list-refresh"
          >
            <RefreshCw size={14} /> Ververs
          </button>
        </div>

        {loading ? (
          <div className="mt-6 flex justify-center"><Loader2 className="animate-spin text-zinc-400" /></div>
        ) : images.length === 0 ? (
          <div className="mt-6 rounded-md border border-dashed border-zinc-300 p-8 text-center text-sm text-zinc-500">
            Nog geen afbeeldingen geüpload. Sleep er een hierboven om te beginnen.
          </div>
        ) : (
          <div className="mt-5 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4" data-testid="image-list">
            {images.map((img) => (
              <div
                key={img.sku}
                className="group relative overflow-hidden rounded-md border border-zinc-200 bg-zinc-50"
                data-testid={`image-card-${img.sku}`}
              >
                <div className="aspect-square overflow-hidden bg-white">
                  <img
                    src={`${BASE}/api/images/sku/${img.sku}`}
                    alt={img.sku}
                    className="h-full w-full object-cover"
                    loading="lazy"
                  />
                </div>
                <div className="p-2 text-center">
                  <div className="font-mono text-xs font-bold text-zinc-900">{img.sku}</div>
                  <div className="text-[10px] text-zinc-500">
                    {Math.round((img.size || 0) / 1024)} KB · {(img.content_type || "").replace("image/", "")}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => handleDelete(img.sku)}
                  data-testid={`image-delete-${img.sku}`}
                  className="absolute right-1.5 top-1.5 rounded-full bg-white/90 p-1.5 text-aramfix-red shadow-sm opacity-0 transition group-hover:opacity-100 hover:bg-white"
                  aria-label="Verwijder"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


// ============= DISTRIBUTORS =============

function DistributorsTab({ api }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending");
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get("/admin/distributors");
      setItems(r.data.distributors || []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Kon distributeurs niet laden.");
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { reload(); }, [reload]);

  const approve = async (d) => {
    const markupStr = window.prompt(
      `Opslag % op kostprijs voor ${d.email} (bv. 30 = +30%):`,
      d.markup_pct != null ? String(d.markup_pct) : "30"
    );
    if (markupStr === null) return;
    const markup = parseFloat(markupStr);
    if (Number.isNaN(markup) || markup < 0 || markup > 200) {
      alert("Ongeldige waarde. Gebruik een getal tussen 0 en 200.");
      return;
    }
    setBusyId(d.id);
    try {
      await api.post(`/admin/distributors/${d.id}/approve`, {
        markup_pct: markup,
      });
      await reload();
    } catch (e) {
      alert(e?.response?.data?.detail || "Goedkeuren mislukt.");
    } finally {
      setBusyId(null);
    }
  };

  const reject = async (d) => {
    if (!window.confirm(`Aanvraag van ${d.email} afwijzen?`)) return;
    setBusyId(d.id);
    try {
      await api.post(`/admin/distributors/${d.id}/reject`);
      await reload();
    } catch (e) {
      alert(e?.response?.data?.detail || "Afwijzen mislukt.");
    } finally {
      setBusyId(null);
    }
  };

  const updateMarkup = async (d) => {
    const markupStr = window.prompt(
      `Nieuwe opslag % voor ${d.email}:`,
      d.markup_pct != null ? String(d.markup_pct) : "30"
    );
    if (markupStr === null) return;
    const markup = parseFloat(markupStr);
    if (Number.isNaN(markup) || markup < 0 || markup > 200) {
      alert("Ongeldige waarde.");
      return;
    }
    setBusyId(d.id);
    try {
      await api.patch(`/admin/distributors/${d.id}`, { markup_pct: markup });
      await reload();
    } catch (e) {
      alert(e?.response?.data?.detail || "Aanpassen mislukt.");
    } finally {
      setBusyId(null);
    }
  };

  const toggleStatus = async (d) => {
    const next = d.status === "active" ? "pending" : "active";
    if (!window.confirm(`Status van ${d.email} wijzigen naar "${next}"?`)) return;
    setBusyId(d.id);
    try {
      await api.patch(`/admin/distributors/${d.id}`, { status: next });
      await reload();
    } catch (e) {
      alert(e?.response?.data?.detail || "Statuswijziging mislukt.");
    } finally {
      setBusyId(null);
    }
  };

  const filtered = filter === "all"
    ? items
    : items.filter((d) => d.status === filter);

  const counts = items.reduce((acc, d) => {
    acc[d.status] = (acc[d.status] || 0) + 1;
    return acc;
  }, {});

  const STATUS_PILL = {
    pending: "border-amber-300 bg-amber-50 text-amber-800",
    active: "border-emerald-300 bg-emerald-50 text-emerald-800",
    rejected: "border-zinc-300 bg-zinc-100 text-zinc-600",
  };

  return (
    <div className="space-y-5" data-testid="distributors-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-display text-2xl font-bold">Distributeurs</h2>
        <button
          type="button"
          onClick={reload}
          className="inline-flex items-center gap-1 text-sm font-semibold text-nibro-sage-dark hover:underline"
          data-testid="distributors-refresh"
        >
          <RefreshCw size={14} /> Ververs
        </button>
      </div>

      <div className="flex flex-wrap gap-2">
        {[
          { k: "pending", label: "In afwachting" },
          { k: "active", label: "Actief" },
          { k: "rejected", label: "Afgewezen" },
          { k: "all", label: "Alle" },
        ].map((f) => (
          <button
            key={f.k}
            type="button"
            onClick={() => setFilter(f.k)}
            data-testid={`distributors-filter-${f.k}`}
            className={`rounded-md px-3 py-1.5 text-xs font-bold uppercase tracking-wide transition ${
              filter === f.k
                ? "bg-nibro-sage text-white"
                : "border border-zinc-300 bg-white text-zinc-600 hover:bg-zinc-50"
            }`}
          >
            {f.label}
            {f.k !== "all" && (
              <span className="ml-1.5 opacity-70">({counts[f.k] || 0})</span>
            )}
          </button>
        ))}
      </div>

      {error && (
        <div className="rounded-md border border-aramfix-red/30 bg-aramfix-red/5 px-4 py-3 text-sm text-aramfix-red">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="animate-spin text-zinc-400" /></div>
      ) : filtered.length === 0 ? (
        <div className="rounded-md border border-dashed border-zinc-300 p-8 text-center text-sm text-zinc-500">
          Geen distributeurs in deze filter.
        </div>
      ) : (
        <div className="overflow-hidden rounded-md border border-zinc-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-left text-[10px] uppercase tracking-wider text-zinc-500">
              <tr>
                <th className="px-3 py-2">Bedrijf / E-mail</th>
                <th className="px-3 py-2">Contact</th>
                <th className="px-3 py-2">Land</th>
                <th className="px-3 py-2">KVK / BTW</th>
                <th className="px-3 py-2 text-right">Opslag %</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Aangevraagd</th>
                <th className="px-3 py-2 text-right">Acties</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((d) => (
                <tr
                  key={d.id}
                  className="border-t border-zinc-100 align-top"
                  data-testid={`distributor-row-${d.id}`}
                >
                  <td className="px-3 py-3">
                    <div className="font-semibold text-zinc-900">{d.company || "—"}</div>
                    <div className="font-mono text-[11px] text-zinc-500">{d.email}</div>
                  </td>
                  <td className="px-3 py-3 text-zinc-700">
                    {d.contact_name || "—"}
                    {d.phone && <div className="text-[11px] text-zinc-500">{d.phone}</div>}
                  </td>
                  <td className="px-3 py-3 text-zinc-700">{d.country || "—"}</td>
                  <td className="px-3 py-3 font-mono text-[11px] text-zinc-600">
                    <div>KVK: {d.kvk || "—"}</div>
                    <div>BTW: {d.vat_number || "—"}</div>
                  </td>
                  <td className="px-3 py-3 text-right font-mono">
                    {d.markup_pct != null ? `${d.markup_pct.toFixed(1)}%` : "—"}
                  </td>
                  <td className="px-3 py-3">
                    <span className={`inline-block rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase ${STATUS_PILL[d.status] || STATUS_PILL.pending}`}>
                      {d.status}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-[11px] text-zinc-500">
                    {d.created_at ? new Date(d.created_at).toLocaleDateString("nl-NL") : "—"}
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex flex-wrap justify-end gap-1">
                      {d.status === "pending" && (
                        <>
                          <button
                            type="button"
                            disabled={busyId === d.id}
                            onClick={() => approve(d)}
                            className="rounded bg-nibro-sage px-2.5 py-1 text-[11px] font-bold text-white hover:bg-nibro-sage-hover disabled:opacity-50"
                            data-testid={`distributor-approve-${d.id}`}
                          >
                            Goedkeuren
                          </button>
                          <button
                            type="button"
                            disabled={busyId === d.id}
                            onClick={() => reject(d)}
                            className="rounded border border-aramfix-red bg-white px-2.5 py-1 text-[11px] font-bold text-aramfix-red hover:bg-aramfix-red/5 disabled:opacity-50"
                            data-testid={`distributor-reject-${d.id}`}
                          >
                            Afwijzen
                          </button>
                        </>
                      )}
                      {d.status === "active" && (
                        <>
                          <button
                            type="button"
                            disabled={busyId === d.id}
                            onClick={() => updateMarkup(d)}
                            className="rounded border border-zinc-300 bg-white px-2.5 py-1 text-[11px] font-semibold text-zinc-700 hover:bg-zinc-50 disabled:opacity-50"
                            data-testid={`distributor-update-${d.id}`}
                          >
                            Opslag aanpassen
                          </button>
                          <button
                            type="button"
                            disabled={busyId === d.id}
                            onClick={() => toggleStatus(d)}
                            className="rounded border border-amber-300 bg-amber-50 px-2.5 py-1 text-[11px] font-semibold text-amber-800 hover:bg-amber-100 disabled:opacity-50"
                            data-testid={`distributor-deactivate-${d.id}`}
                          >
                            Deactiveren
                          </button>
                        </>
                      )}
                      {d.status === "rejected" && (
                        <button
                          type="button"
                          disabled={busyId === d.id}
                          onClick={() => approve(d)}
                          className="rounded border border-nibro-sage bg-white px-2.5 py-1 text-[11px] font-bold text-nibro-sage-dark hover:bg-nibro-sage-pale disabled:opacity-50"
                          data-testid={`distributor-reapprove-${d.id}`}
                        >
                          Heractiveren
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


// ============= Per-language Sitemap Widget =============

const LANG_FLAG = {
  nl: "🇳🇱", en: "🇬🇧", de: "🇩🇪", fr: "🇫🇷", es: "🇪🇸",
  it: "🇮🇹", pl: "🇵🇱", pt: "🇵🇹", sv: "🇸🇪", da: "🇩🇰",
};
const LANG_NAME = {
  nl: "Nederlands", en: "English", de: "Deutsch", fr: "Français", es: "Español",
  it: "Italiano", pl: "Polski", pt: "Português", sv: "Svenska", da: "Dansk",
};

function LanguageSitemapWidget({ api, baseUrl, onCopy }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get("/admin/sitemap-stats")
      .then((r) => { if (alive) setStats(r.data); })
      .catch((e) => {
        if (!alive) return;
        setError(e?.response?.data?.detail || "Kon sitemap-statistieken niet laden");
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [api]);

  if (loading) {
    return (
      <div className="rounded-md border border-zinc-200 bg-white p-6 text-center text-xs text-zinc-500">
        <Loader2 size={14} className="mx-auto animate-spin text-nibro-sage-dark" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="rounded-md border border-amber-200 bg-amber-50 p-4 text-xs text-amber-800">
        ⚠️ {error}
      </div>
    );
  }
  if (!stats) return null;

  const domainMismatch =
    baseUrl && stats.canonical_domain &&
    baseUrl.replace(/^https?:\/\//, "").replace(/^www\./, "") !==
      stats.canonical_domain.replace(/^https?:\/\//, "").replace(/^www\./, "");

  return (
    <div className="rounded-md border border-zinc-200 bg-white p-6" data-testid="seo-language-stats">
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 font-display text-base font-bold">
            <Globe size={16} className="text-nibro-sage-dark" />
            Per-taal sitemap (5 EU-markten)
          </div>
          <p className="mt-0.5 text-[11px] text-zinc-500">
            Live URL-count per taal vanuit <code className="font-mono">/sitemap-{`{lang}`}.xml</code>.
            Submit deze in Google Search Console / Bing Webmaster Tools.
          </p>
        </div>
        <a
          href={stats.sitemap_index_url}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 rounded border border-nibro-sage bg-white px-3 py-1.5 text-[11px] font-bold text-nibro-sage-dark hover:bg-nibro-sage-pale"
          data-testid="open-sitemap-index"
        >
          Open sitemap-index <ExternalLink size={11} />
        </a>
      </div>

      {/* Top-line stats */}
      <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-4">
        <SitemapStat label="Unieke paden" value={stats.total_unique_paths} hint="per taal" />
        <SitemapStat label="Talen actief" value={stats.languages.length} hint="NL · EN · DE · FR · ES" />
        <SitemapStat label="Hreflang-signalen" value={stats.total_hreflang_signals.toLocaleString("nl-NL")} hint="naar Google" />
        <SitemapStat
          label="Canonical domein"
          value={stats.canonical_domain.replace(/^https?:\/\//, "")}
          hint={domainMismatch ? "⚠️ mismatch" : "ok"}
          warn={domainMismatch}
        />
      </div>

      {domainMismatch && (
        <div className="mb-4 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-[11px] leading-relaxed text-amber-900">
          ⚠️ <strong>Domain-mismatch:</strong> de backend heeft <code className="font-mono">{stats.canonical_domain}</code> ingesteld
          maar de preview draait op <code className="font-mono">{baseUrl}</code>. Dat is OK voor preview, maar zorg dat productie
          deze <code className="font-mono">PUBLIC_URL</code> heeft.
        </div>
      )}

      {/* Per-language rows */}
      <div className="overflow-hidden rounded border border-zinc-200">
        <table className="w-full text-xs">
          <thead className="bg-zinc-50 text-left text-[10px] uppercase tracking-wider text-zinc-500">
            <tr>
              <th className="px-3 py-2">Taal</th>
              <th className="px-3 py-2 text-right">URLs</th>
              <th className="px-3 py-2">Sitemap-URL</th>
              <th className="px-3 py-2 text-right">Acties</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100">
            {stats.languages.map((l) => (
              <tr key={l.code} data-testid={`sitemap-row-${l.code}`}>
                <td className="px-3 py-2.5">
                  <span className="mr-2 text-base leading-none">{LANG_FLAG[l.code]}</span>
                  <span className="font-mono text-[10px] font-bold uppercase tracking-wider">{l.code}</span>
                  <span className="ml-1.5 text-zinc-500">{LANG_NAME[l.code]}</span>
                </td>
                <td className="px-3 py-2.5 text-right font-mono font-bold text-zinc-900">
                  {l.url_count}
                </td>
                <td className="px-3 py-2.5">
                  <code className="rounded bg-zinc-50 px-1.5 py-0.5 font-mono text-[10px] text-zinc-700">
                    {l.sitemap_url.replace(stats.canonical_domain, "")}
                  </code>
                </td>
                <td className="px-3 py-2.5">
                  <div className="flex items-center justify-end gap-1.5">
                    <button
                      type="button"
                      onClick={() => onCopy && onCopy(l.sitemap_url)}
                      className="inline-flex items-center gap-1 rounded border border-zinc-200 bg-white px-2 py-1 text-[10px] font-semibold text-zinc-700 hover:bg-zinc-50"
                      data-testid={`copy-sitemap-${l.code}`}
                    >
                      <Copy size={10} /> Kopieer
                    </button>
                    <a
                      href={l.sitemap_url}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 rounded border border-zinc-200 bg-white px-2 py-1 text-[10px] font-semibold text-zinc-700 hover:bg-zinc-50"
                    >
                      <ExternalLink size={10} /> Open
                    </a>
                    <a
                      href={l.sample_homepage}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 rounded border border-zinc-200 bg-white px-2 py-1 text-[10px] font-semibold text-zinc-700 hover:bg-zinc-50"
                      title={`Bezoek ${l.sample_homepage}`}
                    >
                      <Globe size={10} /> Bekijk
                    </a>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Verification meta-tag generator */}
      <VerificationTagsGenerator />
    </div>
  );
}

function SitemapStat({ label, value, hint, warn }) {
  return (
    <div className={`rounded border px-3 py-2.5 ${warn ? "border-amber-300 bg-amber-50" : "border-zinc-200 bg-zinc-50"}`}>
      <div className="font-mono text-[9px] uppercase tracking-wider text-zinc-500">{label}</div>
      <div className={`mt-0.5 font-display text-lg font-bold ${warn ? "text-amber-900" : "text-zinc-900"}`}>{value}</div>
      {hint && <div className="text-[10px] text-zinc-500">{hint}</div>}
    </div>
  );
}

function VerificationTagsGenerator() {
  const [codes, setCodes] = useState({ google: "", bing: "", yandex: "", baidu: "" });
  const [copied, setCopied] = useState("");

  const meta = [
    codes.google && `<meta name="google-site-verification" content="${codes.google}" />`,
    codes.bing && `<meta name="msvalidate.01" content="${codes.bing}" />`,
    codes.yandex && `<meta name="yandex-verification" content="${codes.yandex}" />`,
    codes.baidu && `<meta name="baidu-site-verification" content="${codes.baidu}" />`,
  ].filter(Boolean).join("\n");

  const copy = () => {
    if (!meta) return;
    navigator.clipboard.writeText(meta);
    setCopied("Gekopieerd naar klembord");
    setTimeout(() => setCopied(""), 1800);
  };

  return (
    <div className="mt-6 rounded border border-zinc-200 bg-zinc-50 p-4" data-testid="verification-tag-generator">
      <div className="mb-3 flex items-center justify-between">
        <div>
          <div className="font-display text-sm font-bold">Verificatie meta-tags</div>
          <p className="mt-0.5 text-[10px] text-zinc-500">
            Vul de codes in die je krijgt bij <strong>elke webmaster-tool</strong>. Hieronder verschijnen de meta-tags
            die je in <code className="font-mono">/app/frontend/public/index.html</code> moet plakken (tussen de SEO-comment markers).
          </p>
        </div>
        {copied && <span className="text-[10px] font-bold text-emerald-700">{copied}</span>}
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <VerificationField
          label="Google Search Console"
          help="search.google.com/search-console → Verification → HTML tag → kopieer alleen de content-waarde"
          placeholder="bv. abc123XYZ_kjasdklj"
          value={codes.google}
          onChange={(v) => setCodes({ ...codes, google: v })}
        />
        <VerificationField
          label="Bing Webmaster Tools"
          help="www.bing.com/webmasters → Verify ownership → meta-tag → content-waarde"
          placeholder="bv. A1B2C3D4E5F6..."
          value={codes.bing}
          onChange={(v) => setCodes({ ...codes, bing: v })}
        />
        <VerificationField
          label="Yandex Webmaster"
          help="webmaster.yandex.com → Add site → meta-tag verification"
          placeholder="bv. 1234567890abcdef"
          value={codes.yandex}
          onChange={(v) => setCodes({ ...codes, yandex: v })}
        />
        <VerificationField
          label="Baidu (optioneel — Aziatische markt)"
          help="ziyuan.baidu.com → Site Verification → meta-tag"
          placeholder="optioneel"
          value={codes.baidu}
          onChange={(v) => setCodes({ ...codes, baidu: v })}
        />
      </div>

      {meta ? (
        <div className="mt-4 rounded border border-emerald-300 bg-emerald-50 p-3">
          <div className="mb-2 flex items-center justify-between">
            <div className="font-mono text-[10px] font-bold uppercase tracking-wider text-emerald-800">
              Plak deze in <code>public/index.html</code>
            </div>
            <button
              type="button"
              onClick={copy}
              className="inline-flex items-center gap-1 rounded bg-emerald-700 px-2.5 py-1 text-[10px] font-bold text-white hover:bg-emerald-800"
              data-testid="copy-verification-tags"
            >
              <Copy size={10} /> Kopieer
            </button>
          </div>
          <pre className="overflow-x-auto whitespace-pre-wrap font-mono text-[11px] leading-relaxed text-emerald-900">{meta}</pre>
        </div>
      ) : (
        <div className="mt-4 rounded border border-dashed border-zinc-300 bg-white p-3 text-center text-[11px] text-zinc-500">
          Vul minstens één code in om je meta-tags te genereren.
        </div>
      )}
    </div>
  );
}

function VerificationField({ label, help, placeholder, value, onChange }) {
  return (
    <div>
      <label className="block text-[11px] font-bold uppercase tracking-wider text-zinc-700">{label}</label>
      <p className="mt-0.5 text-[10px] leading-snug text-zinc-500">{help}</p>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value.trim())}
        placeholder={placeholder}
        className="mt-1.5 w-full rounded border border-zinc-200 bg-white px-2.5 py-1.5 font-mono text-[11px] focus:border-nibro-sage focus:outline-none focus:ring-1 focus:ring-nibro-sage/30"
      />
    </div>
  );
}
